import { Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import * as moment from 'jalali-moment';
import { IFileObject } from '../../shared/model/file-object.model';
import { IPhone } from '../../shared/model/phone.model';
import { IFileShare } from '../../shared/model/file-share.model';
import { PhoneService } from '../phone/phone.service';
import { FileShareService } from './file-share.service';

@Component({
  selector: 'app-file-share-send',
  templateUrl: './file-share-send.component.html'
})
export class FileShareSendComponent implements OnInit {
  @Input() fileObject: IFileObject;

  phones: IPhone[] = [];
  history: IFileShare[] = [];
  selectedPhoneId: any = '';
  manualPhone = '';
  expireHours = 48;
  smsTitle = '';
  originalFileTitle = '';
  isSaving = false;
  readonly maxTitleWords = 5;

  constructor(
    public activeModal: NgbActiveModal,
    private phoneService: PhoneService,
    private fileShareService: FileShareService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    const raw =
      (this.fileObject && (this.fileObject.title || this.fileObject.itemName)) || '';
    this.originalFileTitle = (raw || '').trim();
    this.smsTitle = this.limitToMaxWords(this.originalFileTitle, this.maxTitleWords);

    if (this.fileObject && this.fileObject.customer && this.fileObject.customer.id) {
      this.phoneService.findWithCustomerId(this.fileObject.customer.id).subscribe(
        (res: HttpResponse<IPhone[]>) => {
          this.phones = res.body || [];
          if (this.phones.length === 1) {
            this.selectedPhoneId = this.phones[0].id;
          }
        },
        () => {
          this.phones = [];
        }
      );
    }
    if (this.fileObject && this.fileObject.id) {
      this.loadHistory();
    }
  }

  get titleWordCount(): number {
    return this.countWords(this.smsTitle);
  }

  get titleTooLong(): boolean {
    return this.titleWordCount > this.maxTitleWords;
  }

  loadHistory() {
    this.fileShareService.findByFile(this.fileObject.id).subscribe(
      (res: HttpResponse<IFileShare[]>) => {
        this.history = res.body || [];
      },
      () => {
        this.history = [];
      }
    );
  }

  clear() {
    this.activeModal.dismiss('cancel');
  }

  send() {
    if (!this.fileObject || !this.fileObject.id) {
      this.toastr.error('فایل نامعتبر است', 'خطا');
      return;
    }

    const title = (this.smsTitle || '').replace(/\s+/g, ' ').trim();
    if (!title) {
      this.toastr.warning('عنوان پیامک را وارد کنید', 'توجه');
      return;
    }
    if (this.countWords(title) > this.maxTitleWords) {
      this.toastr.warning('عنوان پیامک حداکثر ۵ کلمه می‌تواند باشد', 'توجه');
      return;
    }

    const phoneId = this.selectedPhoneId ? Number(this.selectedPhoneId) : null;
    const phoneNumber = (this.manualPhone || '').trim();

    if (!phoneId && !phoneNumber) {
      this.toastr.warning('لطفاً شماره مشتری را انتخاب یا وارد کنید', 'توجه');
      return;
    }

    if (!this.expireHours && this.expireHours !== 0) {
      this.toastr.warning('مدت اعتبار لینک را وارد کنید', 'توجه');
      return;
    }

    if (this.expireHours < 0) {
      this.toastr.warning('مدت اعتبار لینک نامعتبر است', 'توجه');
      return;
    }

    this.isSaving = true;
    this.spinner.show();
    this.fileShareService
      .send({
        fileId: this.fileObject.id,
        phoneId: phoneId || undefined,
        phoneNumber: phoneId ? undefined : phoneNumber,
        expireHours: this.expireHours,
        fileTitle: title
      })
      .subscribe(
        () => {
          this.isSaving = false;
          this.spinner.hide();
          this.toastr.success('لینک فایل برای مشتری پیامک شد', 'موفق');
          this.activeModal.close('sent');
        },
        (err: HttpErrorResponse) => {
          this.isSaving = false;
          this.spinner.hide();
          const msg =
            (err.error && (err.error.title || err.error.message || err.error.detail)) ||
            'ارسال پیامک ناموفق بود';
          this.toastr.error(msg, 'خطا');
        }
      );
  }

  parseDateJalali(date) {
    if (!date) {
      return '';
    }
    return moment(date).locale('fa').format('YYYY/MM/DD HH:mm');
  }

  statusLabel(status: string) {
    if (status === 'SENT') {
      return 'ارسال شده';
    }
    if (status === 'FAILED') {
      return 'ناموفق';
    }
    if (status === 'PENDING') {
      return 'در انتظار';
    }
    if (status === 'EXPIRED') {
      return 'منقضی شده';
    }
    return status || '-';
  }

  private countWords(value: string): number {
    const trimmed = (value || '').replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      return 0;
    }
    return trimmed.split(' ').length;
  }

  private limitToMaxWords(value: string, max: number): string {
    const trimmed = (value || '').replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      return '';
    }
    const words = trimmed.split(' ');
    if (words.length <= max) {
      return trimmed;
    }
    return words.slice(0, max).join(' ');
  }
}
